import { trackCardEvent } from '../lib/cardEvents.js';
import React, { useState, useEffect } from 'react';
import {
  Save, Share2, Phone, Mail, Globe,
  Linkedin, Twitter, Instagram, Github, Eye,
  User, MapPin, Briefcase,
  Download, FileText, Calendar, Video, Music, ShoppingCart,
  Link as LinkIcon, Youtube, Facebook, MessageCircle, Sun, Moon,
  ExternalLink
} from 'lucide-react';
import type { LucideProps } from 'lucide-react';
import { useAppTranslation } from '../hooks/useAppTranslation.js';
import { withCustomAccent, CARD_FONTS } from '../utils/cardDesign.js';
import {
  sanitizeText,
  sanitizeHTML,
  collectContactPhones,
  buildAddressData,
  buildVCardString,
  buildQrPayload,
  saveQrPayloadToStorage,
  loadQrPayloadFromStorage,
  type CardData,
  type CardLink,
  type AppSettings,
  type ThemeColor
} from '../utils/cardUtils.js';
import {
  normalizeThemeVariantClient,
  applyThemeCssVars,
  getThemeGradient,
  getButtonColor,
  getLinkColor,
  getTextColor,
  darkenHex
} from '../utils/themeUtils.js';

// ---------------------------------------------------------------------------
// Icon map for custom links
// ---------------------------------------------------------------------------
type IconComponent = React.ForwardRefExoticComponent<Omit<LucideProps, 'ref'> & React.RefAttributes<SVGSVGElement>>;

const ICON_MAP: Record<string, IconComponent> = {
  link: LinkIcon,
  download: Download,
  file: FileText,
  calendar: Calendar,
  video: Video,
  music: Music,
  shop: ShoppingCart,
  youtube: Youtube,
  facebook: Facebook,
  whatsapp: MessageCircle,
  globe: Globe
};

// ---------------------------------------------------------------------------
// SocialIcon sub-component
// ---------------------------------------------------------------------------
interface SocialIconProps {
  url?: string | undefined;
  icon: IconComponent;
  label: string;
  brandColor: string;
}

function SocialIcon({ url, icon: Icon, label, brandColor, onOpen }: SocialIconProps & { onOpen?: (label: string) => void }) {
  if (!url) return null;
  return (
    <a href={url} target="_blank" rel="noreferrer" onClick={() => onOpen?.(label)} className="flex flex-col items-center gap-2 group">
      <div
        className="w-full aspect-square rounded-full flex items-center justify-center group-hover:scale-105 transition-all group-hover:shadow-md"
        style={{ backgroundColor: brandColor + '26', border: `1px solid ${brandColor}33` }}
      >
        <Icon className="w-5 h-5" style={{ color: darkenHex(brandColor, 20) }} />
      </div>
      <span className="text-[10px] uppercase font-bold tracking-wider text-text-muted-subtle dark:text-text-muted-dark">{label}</span>
    </a>
  );
}

// ---------------------------------------------------------------------------
// CardDisplay props
// ---------------------------------------------------------------------------
export interface CardDisplayProps {
  data: CardData;
  settings?: AppSettings | undefined;
  darkMode?: boolean | undefined;
  toggleDarkMode?: (() => void) | undefined;
  showAlert?: ((message: string, type?: string, title?: string) => void) | undefined;
}

// ---------------------------------------------------------------------------
// CardDisplay component
// ---------------------------------------------------------------------------
export default function CardDisplay({ data, settings: baseSettings, darkMode, toggleDarkMode, showAlert }: CardDisplayProps) {
  const { t } = useAppTranslation();
  const {
    personal = {},
    contact = {},
    social = {},
    images = {},
    theme: baseTheme = { color: 'indigo' },
    links = [],
    privacy = {}
  } = data;
  // a custom accent colour replaces the preset colour for all lookups below
  const { theme, settings } = withCustomAccent(baseTheme, baseSettings);
  const fontStack = CARD_FONTS[theme.font ?? 'default']?.stack ?? '';

  const themeColor = settings?.theme_colors?.find((c: ThemeColor) => c.name === theme.color);

  const [showQR, setShowQR] = useState(false);
  const [qrMode, setQrMode] = useState<'simple' | 'rich'>(() => {
    if (typeof navigator !== 'undefined') {
      return navigator.onLine ? 'simple' : 'rich';
    }
    return 'simple';
  });
  const [qrSimpleDataUrl, setQrSimpleDataUrl] = useState('');
  const [qrRichDataUrl, setQrRichDataUrl] = useState('');
  const [offlineQrPayload, setOfflineQrPayload] = useState<string | null>(null);
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  const [qrError, setQrError] = useState<string | null>(null);
  const [contactRevealed, setContactRevealed] = useState(false);
  const [showSendOptions, setShowSendOptions] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<Event | null>(null);
  const [isPwaInstalled, setIsPwaInstalled] = useState(false);

  // Lead capture deep links
  const phoneEntries = collectContactPhones(contact);
  const addressData = buildAddressData(contact, personal);
  const locationDisplay = sanitizeText(personal.location || addressData.summary || '').trim();
  const routeLink = addressData.routeQuery
    ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(addressData.routeQuery)}`
    : null;
  const ownerPhone = phoneEntries[0]?.value || '';
  const ownerEmail = contact.email || '';
  const ownerPhoneDigits = ownerPhone.replace(/\D/g, '');

  const whatsappLink =
    ownerPhoneDigits && ownerPhoneDigits.length >= 8
      ? `https://wa.me/${ownerPhoneDigits}?text=${encodeURIComponent('Hi, we met via your Visitenkarl card. My name is ... and my number/email is ...')}`
      : null;

  const emailLink = ownerEmail
    ? `mailto:${ownerEmail}?subject=${encodeURIComponent('My details from Visitenkarl')}&body=${encodeURIComponent('Hi, we met via your Visitenkarl card.\nMy name is ...\nMy phone number is ...\nMy email address is ...')}`
    : null;

  const dropCallLink = ownerPhone ? `tel:${ownerPhone}` : null;

  // Helper functions for obfuscation
  const obfuscateContact = (value: string): string => {
    if (!value) return '';
    return btoa(value);
  };

  const deobfuscateContact = (obfuscated: string): string => {
    if (!obfuscated) return '';
    try {
      return atob(obfuscated);
    } catch {
      return '';
    }
  };

  // Apply app theme variant
  useEffect(() => {
    const variant = normalizeThemeVariantClient(settings?.theme_variant || 'vcard');
    document.body.classList.remove('theme-vcard', 'theme-minimal', 'theme-mint', 'theme-nagarro', 'theme-custom');
    document.body.classList.add(`theme-${variant}`);
    applyThemeCssVars(variant, settings?.app_theme_overrides || {});
  }, [settings]);

  // Set document title
  useEffect(() => {
    const firstName = sanitizeText(personal.firstName || '');
    const lastName = sanitizeText(personal.lastName || '');
    if (firstName || lastName) {
      document.title = `${firstName} ${lastName}`;
    }
  }, [personal]);

  // Dynamic per-card manifest link
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    const path = window.location.pathname || '';
    const slug = path.replace(/^\//, '').split('/')[0];
    if (!slug || slug === 'admin') return;

    const head = document.head;
    let link = document.querySelector('link[rel="manifest"]') as HTMLLinkElement | null;
    const dynamicHref = `/manifest/${slug}.json`;

    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'manifest');
      head.appendChild(link);
    }

    if (link.getAttribute('href') !== dynamicHref) {
      link.setAttribute('href', dynamicHref);
    }
  }, []);

  // Handle PWA install prompt
  useEffect(() => {
    const handleBeforeInstall = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    const handleAppInstalled = () => {
      setIsPwaInstalled(true);
      setDeferredPrompt(null);
    };

    const checkStandalone = () => {
      const isStandalone =
        window.matchMedia('(display-mode: standalone)').matches ||
        (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
      if (isStandalone) {
        setIsPwaInstalled(true);
      }
    };

    checkStandalone();
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const shouldShowInstallButton = !isPwaInstalled;

  // Track online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Handle robots meta tag
  useEffect(() => {
    const shouldBlockRobots = privacy.blockRobots ?? true;
    if (shouldBlockRobots) {
      let metaRobots = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
      if (!metaRobots) {
        metaRobots = document.createElement('meta');
        metaRobots.setAttribute('name', 'robots');
        document.head.appendChild(metaRobots);
      }
      metaRobots.setAttribute('content', 'noindex, nofollow');
    } else {
      const metaRobots = document.querySelector('meta[name="robots"]');
      if (metaRobots) metaRobots.remove();
    }
    return () => {
      const metaRobots = document.querySelector('meta[name="robots"]');
      if (metaRobots) metaRobots.remove();
    };
  }, [privacy.blockRobots]);

  // Fetch QR code when modal opens
  useEffect(() => {
    if (!showQR) return;

    setQrError(null);

    const pathParts =
      typeof window !== 'undefined'
        ? window.location.pathname.substring(1).split('/').filter((p) => p)
        : [];
    const firstPart = pathParts[0] ?? '';
    const lastPart = pathParts[pathParts.length - 1] ?? '';
    const isShortCodeRoute = pathParts.length === 1 && /^[a-zA-Z0-9]{7}$/.test(firstPart);
    const shortCode = data._shortCode || (isShortCodeRoute ? firstPart : null);
    const qrIdentifier = shortCode || (pathParts.length > 0 ? lastPart : '');

    const payload = buildQrPayload(shortCode, { personal, contact, social, images, theme });
    saveQrPayloadToStorage(payload);

    const online = typeof navigator !== 'undefined' ? navigator.onLine : true;

    if (!online) {
      if (qrMode === 'rich') {
        const cached = loadQrPayloadFromStorage();
        if (cached) setOfflineQrPayload(cached);
      }
      return;
    }

    if (qrMode === 'simple' && !qrSimpleDataUrl) {
      fetch(`/api/qr/${qrIdentifier}`, { method: 'GET', credentials: 'include' })
        .then((res) => {
          if (!res.ok) throw new Error(`QR request failed: ${res.status}`);
          return res.json() as Promise<{ qrCode?: string }>;
        })
        .then((responseData) => {
          if (responseData?.qrCode) setQrSimpleDataUrl(responseData.qrCode);
        })
        .catch((err: unknown) => {
          console.error('Failed to fetch simple QR code:', err);
          setQrError('Unable to load link-only QR right now. Please try again in a moment.');
        });
    } else if (qrMode === 'rich' && !qrRichDataUrl) {
      fetch(`/api/qr/${qrIdentifier}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ payload })
      })
        .then((res) => {
          if (!res.ok) throw new Error(`QR request failed: ${res.status}`);
          return res.json() as Promise<{ qrCode?: string }>;
        })
        .then((responseData) => {
          if (responseData?.qrCode) setQrRichDataUrl(responseData.qrCode);
          saveQrPayloadToStorage(payload);
          setOfflineQrPayload(payload);
        })
        .catch((err: unknown) => {
          console.error('Failed to fetch rich QR code:', err);
          const cached = loadQrPayloadFromStorage();
          if (cached) setOfflineQrPayload(cached);
          setQrError('Unable to load full-details QR right now. Your last saved details are still available offline.');
        });
    }
  }, [showQR, qrMode, qrSimpleDataUrl, qrRichDataUrl, personal, contact, social, images, theme, data]);

  // Extract short code and URL for display
  const pathParts =
    typeof window !== 'undefined'
      ? window.location.pathname.substring(1).split('/').filter((p) => p)
      : [];
  const displayFirstPart = pathParts[0] ?? '';
  const isShortCodeRoute = pathParts.length === 1 && /^[a-zA-Z0-9]{7}$/.test(displayFirstPart);
  const shortCode = data._shortCode || (isShortCodeRoute ? displayFirstPart : null);
  const shortUrl = shortCode && typeof window !== 'undefined' ? `${window.location.origin}/${shortCode}` : '';
  const cardName = `${personal.firstName || ''} ${personal.lastName || ''}`.trim();
  const company = personal.company || '';

  const generateVCard = () => {
    trackCardEvent(shortCode, 'save_contact');
    const vcard = buildVCardString({ personal, contact, cardUrl: shortUrl });
    const firstName = sanitizeText(personal.firstName || 'contact');
    const lastName = sanitizeText(personal.lastName || 'card');
    const blob = new Blob([vcard], { type: 'text/vcard' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${firstName}_${lastName}.vcf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const currentQrDataUrl = qrMode === 'simple' ? qrSimpleDataUrl : qrRichDataUrl;

  // QR code full-screen view
  if (showQR) {
    return (
      <div className="fixed inset-0 bg-card dark:bg-card-dark flex flex-col text-center overflow-hidden lg:rounded-[22px] z-50 min-h-screen lg:min-h-0 lg:h-auto">
        <div className="flex flex-col items-center justify-start pt-8 px-4 pb-8 lg:pt-8 lg:flex-shrink-0">
          <div className="w-[90%]">
            <div className="w-full bg-input-bg dark:bg-input-bg-dark p-3 rounded-input border-thick border-border-subtle dark:border-border-dark flex items-center justify-center overflow-hidden">
              {currentQrDataUrl ? (
                <img src={currentQrDataUrl} className="w-full aspect-square mix-blend-multiply dark:mix-blend-normal" alt={t('publicCard.qr.alt', 'QR code')} />
              ) : qrMode === 'rich' && offlineQrPayload ? (
                <div className="w-full aspect-square flex flex-col items-center justify-center text-text-muted-subtle dark:text-text-secondary-dark text-xs space-y-1">
                  <span>{isOnline ? t('publicCard.qr.savedDetails', 'Saved details') : t('publicCard.qr.offlineMode', 'Offline mode')}</span>
                  <span className="text-[10px] opacity-80 px-1">
                    {t('publicCard.qr.savedDetailsDescription', "This code includes your saved Visitenkarl details and works even when you're offline.")}
                  </span>
                </div>
              ) : !isOnline && qrMode === 'simple' && !qrSimpleDataUrl ? (
                <div className="w-full aspect-square flex flex-col items-center justify-center text-text-muted-subtle dark:text-text-secondary-dark text-xs text-center space-y-1">
                  <span>{t('publicCard.qr.linkOnlyOnline', "Link-only QR is available when you're online.")}</span>
                  <span className="text-[10px] opacity-80 px-1">
                    {t('publicCard.qr.switchToFullDetails', 'Switch to "Full details" to store contact details directly in the QR code.')}
                  </span>
                </div>
              ) : (
                <div className="w-full aspect-square flex items-center justify-center text-text-muted-subtle dark:text-text-muted-dark text-xs text-center">
                  {isOnline
                    ? qrError || t('publicCard.qr.loading', 'Loading your QR code...')
                    : t('publicCard.qr.connectOnce', 'Connect once to generate and save your QR code for offline use.')}
                </div>
              )}
            </div>
          </div>

          <div className="mt-6 space-y-2 px-4">
            {cardName && (
              <h2 className="text-xl font-bold text-text-primary dark:text-text-primary-dark">{cardName}</h2>
            )}
            {company && (
              <div className="text-text-muted dark:text-text-muted-dark text-sm">{company}</div>
            )}
            {shortUrl && (
              <div className="text-text-muted-subtle dark:text-text-secondary-dark text-xs font-mono break-all">{shortUrl}</div>
            )}
          </div>

          {!isOnline && offlineQrPayload && (
            <p className="text-xs text-text-muted dark:text-text-muted-dark mt-4 px-4">
              {t('publicCard.qr.usingOffline', 'Using last saved QR details (offline)...')}
            </p>
          )}
        </div>

        <div className="mt-auto pb-4 px-4 space-y-3 lg:pb-4 lg:pt-4 lg:flex-shrink-0">
          <div className="flex w-full items-center justify-center gap-1 bg-surface dark:bg-surface-dark rounded-full p-1 text-[11px] max-w-md mx-auto">
            <button
              type="button"
              onClick={() => setQrMode('simple')}
              className={`flex-1 px-2.5 py-1 rounded-full font-medium transition-colors ${
                qrMode === 'simple'
                  ? 'bg-card dark:bg-main-dark text-text-primary dark:text-text-primary-dark shadow-sm'
                  : 'text-text-muted dark:text-text-secondary-dark'
              }`}
            >
              {t('publicCard.qr.linkOnly', 'Link only')}
            </button>
            <button
              type="button"
              onClick={() => setQrMode('rich')}
              className={`flex-1 px-2.5 py-1 rounded-full font-medium transition-colors ${
                qrMode === 'rich'
                  ? 'bg-card dark:bg-main-dark text-text-primary dark:text-text-primary-dark shadow-sm'
                  : 'text-text-muted dark:text-text-secondary-dark'
              }`}
            >
              {t('publicCard.qr.fullDetails', 'Full details')}
            </button>
          </div>

          <button
            onClick={() => setShowQR(false)}
            className="w-full max-w-md mx-auto py-3 bg-surface dark:bg-surface-dark text-text-primary dark:text-text-primary-dark font-bold rounded-input hover:bg-surface dark:hover:bg-surface-dark text-sm transition-colors"
          >
            {t('common.actions.close', 'Close')}
          </button>

          <div className="bg-card dark:bg-card-dark text-center space-y-2 mt-[24px] mb-[12px]">
            <div className="flex justify-center py-4">
              <img src="/graphics/visitenkarl-logo.svg" alt={t('common.alt.brandLogo', 'Visitenkarl logo')} className="h-8 md:h-10 w-auto dark:hidden brand-logo" />
              <img src="/graphics/visitenkarl-logo-dark.svg" alt={t('common.alt.brandLogo', 'Visitenkarl logo')} className="h-8 md:h-10 w-auto hidden dark:block brand-logo" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Normal card view — Variant B "Card Hero"
  const accentColor = themeColor?.buttonStyle || getButtonColor(theme.color, settings);
  const accentTextColor = themeColor?.textStyle || getTextColor(theme.color, settings);
  const linkAccent = themeColor?.linkStyle || getLinkColor(theme.color, settings);
  const accentHover = darkenHex(accentColor, 10);

  // VCARD 5.0 look: in dark mode the whole card is one continuous navy gradient
  const darkGradient = 'linear-gradient(180deg, #1a2148 0%, #0d1233 45%, #06041f 100%)';

  return (
    <div
      className="flex flex-col h-full bg-card dark:bg-card-dark"
      style={{
        ...(fontStack ? { fontFamily: fontStack } : {}),
        ...(darkMode ? { background: darkGradient } : {}),
      }}
    >
      {/* Banner — h-44, full bleed (VCARD profile look); in dark mode it melts into the gradient */}
      <div className={`h-44 w-full relative flex-shrink-0 ${darkMode ? '' : 'bg-surface dark:bg-surface-dark'}`}>
        {images.banner ? (
          <>
            <img src={images.banner} className="w-full h-full object-cover" alt={t('publicCard.images.bannerAlt', 'Card banner')} />
            {darkMode && (
              <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(6,4,31,0) 35%, rgba(6,4,31,0.9) 100%)' }} />
            )}
          </>
        ) : darkMode ? null : (
          <div className="w-full h-full" style={{ background: getThemeGradient(theme.color, settings) }} />
        )}
        {/* Top-right action buttons */}
        <div className="absolute top-4 right-4 flex gap-2">
          <button
            onClick={toggleDarkMode}
            className="bg-white/25 dark:bg-black/30 backdrop-blur-md p-2.5 rounded-full text-white hover:bg-white/40 dark:hover:bg-black/50 transition-all border border-white/20 shadow-sm"
            aria-label={darkMode ? 'Light mode' : 'Dark mode'}
          >
            {darkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
          <button
            onClick={() => { trackCardEvent(shortCode, 'show_qr'); setShowQR(true); }}
            className="bg-white/25 dark:bg-black/30 backdrop-blur-md p-2.5 rounded-full text-white hover:bg-white/40 dark:hover:bg-black/50 transition-all border border-white/20 shadow-sm"
            aria-label={t('publicCard.actions.showQr', 'Show QR code')}
          >
            <Share2 className="w-4 h-4" />
          </button>
          {shouldShowInstallButton && (
            <button
              onClick={async () => {
                if (deferredPrompt) {
                  try {
                    const prompt = deferredPrompt as Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: string }> };
                    await prompt.prompt();
                    const { outcome } = await prompt.userChoice;
                    if (outcome === 'accepted') setIsPwaInstalled(true);
                  } catch (e) {
                    console.error('Install prompt failed:', e);
                    if (typeof showAlert === 'function') {
                      showAlert(
                        t('pwa.install.failedMessage', 'Install prompt failed. Please use your browser menu to install.'),
                        'error',
                        t('pwa.install.failedTitle', 'Install Failed')
                      );
                    }
                  } finally {
                    setDeferredPrompt(null);
                  }
                } else {
                  const isStandalone =
                    window.matchMedia('(display-mode: standalone)').matches ||
                    (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
                  if (isStandalone) {
                    if (typeof showAlert === 'function') {
                      showAlert(
                        t('pwa.install.alreadyInstalledMessage', 'This app is already installed on your device.'),
                        'info',
                        t('pwa.install.alreadyInstalledTitle', 'Already Installed')
                      );
                    }
                    setIsPwaInstalled(true);
                  } else {
                    const userAgent = navigator.userAgent.toLowerCase();
                    let instructions = `${t('pwa.install.instructionsIntro', 'To install this app:')}\n\n`;
                    if (userAgent.includes('chrome') || userAgent.includes('edge')) {
                      instructions += t('pwa.install.instructions.chrome', 'Chrome/Edge: Click the three dots menu in the address bar, then select "Install app".');
                    } else if (userAgent.includes('firefox')) {
                      instructions += t('pwa.install.instructions.firefox', 'Firefox: Click the menu button, then select "Install".');
                    } else if (userAgent.includes('safari')) {
                      instructions += t('pwa.install.instructions.safari', 'Safari (iOS): Tap the Share button, then "Add to Home Screen".');
                    } else {
                      instructions += t('pwa.install.instructions.generic', 'Open your browser menu and look for "Install app" or "Add to Home Screen".');
                    }
                    if (typeof showAlert === 'function') {
                      showAlert(instructions, 'info', t('pwa.install.title', 'Install Visitenkarl'));
                    } else {
                      alert(instructions);
                    }
                  }
                }
              }}
              className="bg-white/25 dark:bg-black/30 backdrop-blur-md p-2.5 rounded-full text-white hover:bg-white/40 dark:hover:bg-black/50 transition-all border border-white/20 shadow-sm"
              aria-label={t('pwa.install.buttonLabel', 'Install app for offline access')}
            >
              <Download className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Card body */}
      <div className="flex-1 flex flex-col min-h-0 px-6 pb-6">
        {/* Avatar — left-aligned, overlapping the banner (VCARD profile look) */}
        {(() => {
          const initials = [personal.firstName, personal.lastName]
            .map((n) => sanitizeText(n || '').trim()[0] || '')
            .join('')
            .toUpperCase();
          return (
            <div className="w-32 h-32 min-h-[8rem] rounded-full border-4 border-card dark:border-white/75 shadow-xl overflow-hidden bg-card dark:bg-transparent -mt-16 relative z-10 mb-4 flex-shrink-0">
              {images.avatar ? (
                <img src={images.avatar} className="w-full h-full object-cover" alt={t('publicCard.images.avatarAlt', 'Profile avatar')} />
              ) : (
                <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: darkMode ? '#252c58' : accentColor + '20' }}>
                  {initials ? (
                    <span className="text-4xl font-black tracking-tight" style={{ color: accentColor }}>{initials}</span>
                  ) : (
                    <User className="w-12 h-12 text-text-muted-subtle dark:text-text-muted-dark" />
                  )}
                </div>
              )}
            </div>
          );
        })()}

        {/* Name / title / company — left-aligned (VCARD profile look) */}
        <div className="space-y-1 mb-8">
          <h1 className="text-2xl sm:text-3xl dark:text-3xl dark:sm:text-4xl font-bold text-text-primary dark:text-white tracking-tight [overflow-wrap:anywhere]">
            {sanitizeText(`${personal.firstName || ''} ${personal.lastName || ''}`).trim() || t('publicCard.fallbacks.untitled', 'Untitled')}
          </h1>
          {sanitizeText(personal.title || '') && (
            <div className="text-lg font-medium" style={{ color: accentTextColor }}>
              {sanitizeText(personal.title || '')}
            </div>
          )}
          {sanitizeText(personal.company || '') && (
            <div
              className="flex items-center text-text-muted dark:text-text-muted-dark text-sm gap-2"
              style={darkMode ? { color: linkAccent } : undefined}
            >
              <Briefcase className="w-4 h-4 flex-shrink-0" />
              <span>{sanitizeText(personal.company || '')}</span>
            </div>
          )}
          {locationDisplay && (
            <div className="flex items-center text-text-muted-subtle dark:text-text-muted-dark text-sm gap-2 mt-1">
              <MapPin className="w-4 h-4 flex-shrink-0" />
              <span>{locationDisplay}</span>
            </div>
          )}
        </div>

        {/* Bio */}
        {personal.bio && (
          <div className="mb-8">
            <p
              className="text-text-secondary dark:text-text-secondary-dark leading-relaxed text-sm"
              dangerouslySetInnerHTML={{ __html: sanitizeHTML(personal.bio) }}
            />
          </div>
        )}

        {/* Action grid: Save Contact spans two columns, Email/Phone/Route share a row */}
        {(() => {
          const requireInteraction = privacy.requireInteraction ?? false;
          const shouldShowVCF = !requireInteraction || contactRevealed;
          const useObfuscation = privacy.clientSideObfuscation ?? true;
          const hasEmail = Boolean(contact.email);
          const hasPhone = phoneEntries.length > 0;
          const showRoute = Boolean(routeLink && data.contact?.showRouteButton);

          const secondaryCls = 'flex items-center justify-center gap-2 py-3.5 rounded-full font-semibold bg-surface dark:bg-white/5 text-text-primary dark:text-white transition-colors border border-border dark:border-white/15';

          return (
            <div className="grid grid-cols-2 gap-3 mb-8">
              {shouldShowVCF && (
                <button
                  onClick={generateVCard}
                  className="col-span-2 flex items-center justify-center gap-2 py-3.5 rounded-full font-bold text-white shadow-lg transition-all active:scale-[0.98]"
                  style={{ backgroundColor: accentColor }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = accentHover; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = accentColor; }}
                >
                  <Save className="w-5 h-5" />
                  {t('publicCard.actions.saveContact', 'Kontakt speichern')}
                </button>
              )}

              {requireInteraction && !contactRevealed && (hasEmail || hasPhone) ? (
                <button
                  onClick={() => setContactRevealed(true)}
                  className={`col-span-2 ${secondaryCls}`}
                >
                  <Eye className="w-5 h-5" /> {t('publicCard.actions.showDetails', 'See my details')}
                </button>
              ) : (
                <>
                  {hasEmail && (() => {
                    const emailValue = contact.email!;
                    const emailData = useObfuscation ? obfuscateContact(emailValue) : '';
                    return (
                      <a
                        href={useObfuscation ? '#' : `mailto:${emailValue}`}
                        data-email={useObfuscation ? emailData : undefined}
                        onClick={(e) => {
                          trackCardEvent(shortCode, 'click_email');
                          if (useObfuscation) {
                            e.preventDefault();
                            const actual = deobfuscateContact((e.currentTarget as HTMLAnchorElement).dataset.email || '');
                            window.location.href = `mailto:${actual}`;
                          }
                        }}
                        className={secondaryCls}
                      >
                        <Mail className="w-5 h-5" /> {t('publicCard.actions.email', 'E-Mail')}
                      </a>
                    );
                  })()}
                  {phoneEntries.map((phoneEntry, index) => {
                    const phoneValue = phoneEntry.value;
                    const phoneLabel = phoneEntry.label || (index === 0 ? t('publicCard.actions.call', 'Mobil') : 'Telefon');
                    const phoneData = useObfuscation && phoneValue ? obfuscateContact(phoneValue) : '';
                    return (
                      <a
                        key={`phone-${index}`}
                        href={useObfuscation ? '#' : `tel:${phoneValue}`}
                        data-phone={useObfuscation ? phoneData : undefined}
                        onClick={(e) => {
                          trackCardEvent(shortCode, 'click_phone', phoneLabel);
                          if (useObfuscation) {
                            e.preventDefault();
                            const actual = deobfuscateContact((e.currentTarget as HTMLAnchorElement).dataset.phone || '');
                            window.location.href = `tel:${actual}`;
                          }
                        }}
                        className={secondaryCls}
                      >
                        <Phone className="w-5 h-5" /> {phoneLabel}
                      </a>
                    );
                  })}
                  {showRoute && (
                    <a href={routeLink!} target="_blank" rel="noreferrer" onClick={() => trackCardEvent(shortCode, 'click_route')} className={secondaryCls}>
                      <MapPin className="w-5 h-5" /> {t('publicCard.actions.route', 'Route')}
                    </a>
                  )}
                </>
              )}
            </div>
          );
        })()}

        {/* Send your details CTA */}
        <div className="mb-6">
          <button
            type="button"
            onClick={() => setShowSendOptions((open) => !open)}
            className="w-full flex items-center justify-center gap-2 py-3.5 rounded-full font-semibold bg-confirm text-confirm-text dark:bg-white/10 dark:text-white hover:opacity-90 transition-colors shadow-lg active:scale-[0.98]"
          >
            <MessageCircle className="w-5 h-5" />
            {showSendOptions
              ? t('publicCard.actions.hideSendOptions', 'Hide send options')
              : t('publicCard.actions.sendDetails', 'Send your details')}
          </button>

          {showSendOptions && (
            <div className="mt-3 rounded-2xl border border-border dark:border-white/10 bg-surface dark:bg-white/5 overflow-hidden">
              {whatsappLink && (
                <a
                  href={whatsappLink}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => trackCardEvent(shortCode, 'send_whatsapp')}
                  className="flex items-center gap-3 px-4 py-3.5 font-semibold text-sm text-white bg-success dark:bg-success-dark hover:opacity-90 transition-opacity"
                >
                  <MessageCircle className="w-5 h-5 flex-shrink-0" />
                  {t('publicCard.actions.whatsApp', 'WhatsApp me your number')}
                </a>
              )}
              {emailLink && (
                <a
                  href={emailLink}
                  onClick={() => trackCardEvent(shortCode, 'send_email')}
                  className="flex items-center gap-3 px-4 py-3.5 font-semibold text-sm text-text-primary dark:text-text-primary-dark hover:bg-border/30 dark:hover:bg-border-dark/30 transition-colors border-t border-border dark:border-border-dark"
                >
                  <Mail className="w-5 h-5 flex-shrink-0 text-text-muted dark:text-text-muted-dark" />
                  {t('publicCard.actions.emailMe', 'Email me your details')}
                </a>
              )}
              {dropCallLink && (
                <a
                  href={dropCallLink}
                  onClick={() => trackCardEvent(shortCode, 'send_call')}
                  className="flex items-center gap-3 px-4 py-3.5 font-semibold text-sm text-text-primary dark:text-text-primary-dark hover:bg-border/30 dark:hover:bg-border-dark/30 transition-colors border-t border-border dark:border-border-dark"
                >
                  <Phone className="w-5 h-5 flex-shrink-0 text-text-muted dark:text-text-muted-dark" />
                  {t('publicCard.actions.dropcall', 'Drop call me your number')}
                </a>
              )}
              <p className="px-4 py-2.5 text-[11px] text-text-muted dark:text-text-muted-dark border-t border-border dark:border-border-dark">
                {t('publicCard.helper.sharePrivacy', 'Only shared with me, never sold.')}
              </p>
            </div>
          )}
        </div>

        {/* Custom links — icon-left rows with hover lift */}
        {links.length > 0 && (
          <div className="flex flex-col gap-2.5 mb-6">
            {links.map((link: CardLink) => {
              const color = settings?.theme_colors?.find((c: ThemeColor) => c.name === theme.color);
              const iconVal = link.icon || '';
              const isImgIcon = iconVal.startsWith('http') || iconVal.startsWith('data:') || iconVal.startsWith('/');
              const IconComponent = isImgIcon ? null : (ICON_MAP[iconVal] || LinkIcon);
              const iconNode = isImgIcon ? (
                <img src={iconVal} alt="" className="w-5 h-5 object-contain rounded" />
              ) : (
                IconComponent && <IconComponent className="w-4 h-4" />
              );
              const linkColor = color?.linkStyle || getLinkColor(theme.color, settings);
              return (
                <a
                  key={link.id}
                  href={link.url}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => trackCardEvent(shortCode, 'click_link', link.title || undefined)}
                  className="flex items-center gap-3.5 px-4 py-3.5 rounded-2xl bg-surface dark:bg-white/5 border border-border dark:border-white/10 transition-all hover:-translate-y-0.5 hover:shadow-md active:scale-[0.99] group"
                >
                  {/* Icon square */}
                  <span
                    className="flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: linkColor + '18', color: linkColor }}
                  >
                    {iconNode}
                  </span>
                  <span className="font-semibold text-sm flex-1 text-text-primary dark:text-text-primary-dark">{sanitizeText(link.title || '')}</span>
                  <ExternalLink className="w-3.5 h-3.5 opacity-30 group-hover:opacity-60 flex-shrink-0 text-text-muted dark:text-text-muted-dark transition-opacity" />
                </a>
              );
            })}
          </div>
        )}

        {/* Social icons — theme-coloured circles (VCARD profile look) */}
        {[contact.website, social.linkedin, social.twitter, social.instagram, social.github].some(Boolean) && (
          <div className="grid grid-cols-4 gap-3 mb-8">
            <SocialIcon url={contact.website}  icon={Globe}     label={t('publicCard.social.website', 'Web')}       brandColor={accentColor} onOpen={(l) => trackCardEvent(shortCode, 'click_social', l)} />
            <SocialIcon url={social.linkedin}  icon={Linkedin}  label={t('publicCard.social.linkedin', 'LinkedIn')} brandColor={accentColor} onOpen={(l) => trackCardEvent(shortCode, 'click_social', l)} />
            <SocialIcon url={social.twitter}   icon={Twitter}   label={t('publicCard.social.twitter', 'X')}         brandColor={accentColor} onOpen={(l) => trackCardEvent(shortCode, 'click_social', l)} />
            <SocialIcon url={social.instagram} icon={Instagram} label={t('publicCard.social.instagram', 'Insta')}   brandColor={accentColor} onOpen={(l) => trackCardEvent(shortCode, 'click_social', l)} />
            <SocialIcon url={social.github}    icon={Github}    label={t('publicCard.social.github', 'GitHub')}     brandColor={accentColor} onOpen={(l) => trackCardEvent(shortCode, 'click_social', l)} />
          </div>
        )}

        {/* Brand logo — centered, reduced opacity */}
        <div className="mt-auto pt-4 flex justify-center opacity-50">
          <img src="/graphics/visitenkarl-logo.svg" alt={t('common.alt.brandLogo', 'Visitenkarl logo')} className="h-7 w-auto dark:hidden brand-logo" />
          <img src="/graphics/visitenkarl-logo-dark.svg" alt={t('common.alt.brandLogo', 'Visitenkarl logo')} className="h-7 w-auto hidden dark:block brand-logo" />
        </div>
      </div>
    </div>
  );
}
